// ── ADMIN: ACCOUNTS (parent list + parent detail) ────────────────────
// Heavily cross-referenced by other domains: route/viewParent/viewPerformer
// (core/routing.js) are cross-cutting nav primitives, deliberately left as
// onclick="" — see onclick-guardrail memory. downloadContract
// (admin-contracts.js) is a real import, wired via VIEW_INIT below instead
// of window/onclick.
// childModal/dropModal/billingSwitchModal/enrollProgramModal
// (admin-student-actions.js) are only ever reached via onclick="" here, so
// none of them need a JS import.
// sendInvitation/resendInvitation/toggleActive/stripeModal/bulkInvite
// (admin-invitations.js) are real imports, wired via VIEW_INIT below instead
// of window/onclick. renderAccountTable is still called directly (not via
// onclick) from admin-invitations.js's confirmBulkInvite/saveStripeId/
// unlinkStripe/toggleActive, so it stays window-exposed below — that's a
// separate pre-existing cross-file JS-call pattern, unrelated to the
// onclick migration. EMAIL_LABELS/emailLabel moved here from the old Admin
// Dashboard section — this is their only consumer, so they're local rather
// than shared. chargeCardConfirm (admin-dashboard.js) is a real import now,
// wired via VIEW_INIT below.
import { apiFetch } from '../core/api.js';
import { DB, PROGRAMS, US_STATES, session, currentParentId, currentView } from '../core/state.js';
import { fmtD, fmtLocalDT } from '../core/format.js';
import { kidPrograms, childName, isValidEmail, isParent, canEdit, formatAddress, isArchived } from '../core/domain-utils.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT, route, refresh } from '../core/routing.js';
import { inviteState, inviteStatePill, sendInvitation, resendInvitation, toggleActive, stripeModal, bulkInvite } from './admin-invitations.js';
import { CARD_LOGOS, cardModalForUser } from './billing-shared.js';
import { programPill } from './admin-registrations.js';
import { downloadContract } from './admin-contracts.js';
import { chargeCardConfirm } from './admin-dashboard.js';
import { viewAsParent } from '../core/auth.js';
import { archiveFamilyModal, unarchiveFamily, archivedPill, archivedBanner } from './admin-archive.js';

const EMAIL_LABELS = {
  account_invite:           'Portal invitation',
  registration_approved:    'Contract ready to sign',
  reg_reminder_day3:        'Registration reminder – Day 3',
  reg_reminder_day6:        'Registration reminder – Day 6',
  reg_reminder_day7:        'Registration reminder – final day',
  returning_reminder_day1:  'Enrollment reminder',
  returning_reminder_day4:  'Enrollment reminder',
  returning_reminder_day8:  'Enrollment reminder',
  returning_reminder_day15: 'Enrollment reminder – final',
  materials_fee_due:        'Materials fee due',
  registration_complete:    'Enrollment complete',
  payment_failed:           'Payment failed',
  card_expiring:            'Card expiring soon',
};
const emailLabel = type => EMAIL_LABELS[type] || type.replace(/_/g, ' ');

VIEWS['a-parent-detail'] = () => {
  const u = DB.users.find(x => x.id === currentParentId);
  if (!u) return '<p class="empty">Account not found.</p>';
  const kids    = DB.children.filter(c => c.accountId === u.id);
  const charges = DB.charges.filter(c => c.accountId === u.id).slice().sort((a,b) => b.date.localeCompare(a.date));
  const due     = charges.filter(c => c.status === 'due').reduce((s,c) => s + c.amount, 0);
  const linked  = !!(u.stripeId && u.stripeId.startsWith('cus_'));

  const byDate = {};
  charges.forEach(c => { (byDate[c.date] = byDate[c.date] || []).push(c); });
  const allInvoices = Object.entries(byDate).sort((a,b) => b[0].localeCompare(a[0]));
  const invoices = allInvoices.slice(0, 4);

  return `
    <div class="page-head" style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:10px">
      <div>
        <span class="eyebrow check-row" onclick="route('a-accounts')">‹ Parents</span>
        <h2 class="serif">${esc(u.firstName)} <em>${esc(u.lastName)}</em></h2>
      </div>
      <button class="btn-ghost btn-sm" style="width:auto;white-space:nowrap" data-action="view-as-parent" data-account="${u.id}">View as parent</button>
    </div>

    ${isArchived(u) ? archivedBanner(u, { kind: 'family', onRestoreAction: 'unarchive-family' }) : ''}

    ${due ? `
    <div class="card" style="margin-bottom:18px;border-color:rgba(248,113,113,0.35);display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap">
      <div>
        <div style="font-family:'Libre Baskerville',serif;font-size:17px;color:var(--cream)">${esc(u.firstName)} has an outstanding balance of ${fmt$(due)}</div>
        <div style="font-size:13px;color:var(--muted);margin-top:3px">${u.card
          ? `Charge the ${esc(u.card.brand)} ending in ${esc(u.card.last4)} on file, or review the breakdown in Billing history below.`
          : `No card is on file for this account. Add one under Card on file before charging.`}</div>
      </div>
      ${canEdit(session) ? (u.card
        ? `<button class="btn" style="width:auto;white-space:nowrap;padding:11px 22px;flex-shrink:0" data-action="charge-card-confirm" data-account="${u.id}" data-name="${esc(u.firstName + ' ' + u.lastName)}" data-total="${due}">Charge card</button>`
        : `<button class="btn-ghost" style="width:auto;white-space:nowrap;padding:11px 22px;flex-shrink:0" data-action="card-modal-for-user" data-account="${u.id}">Add card</button>`) : ''}
    </div>` : ''}

    <div class="grid-2" style="align-items:start">
      <div style="display:flex;flex-direction:column;gap:18px">

        <div class="card">
          <div class="card-hd">Contact ${canEdit(session) ? `<button class="btn-ghost btn-sm" data-action="acct-modal" data-account="${u.id}">Edit</button>` : ''}</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px 24px">
            <div><div class="detail-label">Name</div><div style="font-size:14px;color:var(--cream)">${esc(u.firstName)} ${esc(u.lastName)}</div></div>
            <div><div class="detail-label">Email</div><div style="font-size:14px;color:var(--cream)">${esc(u.email)}</div></div>
            <div><div class="detail-label">Phone</div><div style="font-size:14px;color:var(--cream)">${esc(u.phone) || '—'}</div></div>
            <div style="grid-column:1 / -1"><div class="detail-label">Address</div><div style="font-size:14px;color:var(--cream);white-space:pre-line">${formatAddress(u) ? esc(formatAddress(u)) : '—'}</div></div>
            <div><div class="detail-label">Access</div>${u.active ? inviteStatePill(inviteState(u)) : (isArchived(u) ? '<span class="pill pill-off">Archived</span>' : '<span class="pill pill-off">Deactivated</span>')}</div>
            <div><div class="detail-label">Last login</div><div style="font-size:14px;color:var(--cream)">${fmtLocalDT(u.lastLogin)}</div></div>
          </div>
          ${canEdit(session) && !isArchived(u) ? `
            <div style="margin-top:16px;padding-top:14px;border-top:1px solid var(--border-soft);display:flex;gap:8px;flex-wrap:wrap">
              ${u.active && inviteState(u) === 'not_invited'
                ? `<button class="btn-ghost btn-sm" data-action="send-invitation" data-account="${u.id}">Send invitation</button>`
                : u.active ? `<button class="btn-ghost btn-sm" data-action="resend-invitation" data-account="${u.id}">Resend invitation</button>` : ''}
              <button class="btn-ghost btn-sm ${u.active ? 'btn-danger' : ''}" data-action="toggle-active" data-account="${u.id}">${u.active ? 'Deactivate account' : 'Reactivate account'}</button>
              <button class="btn-ghost btn-sm btn-danger" data-action="archive-family" data-account="${u.id}">Archive family</button>
            </div>` : ''}
        </div>

        <div class="card">
          <div class="card-hd">Performers</div>
          ${kids.length ? kids.map(k => {
            const ps = kidPrograms(k).map(id => PROGRAMS.find(p => p.id===id)).filter(Boolean);
            return `
            <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid var(--border-soft)">
              <div>
                <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
                  <span style="font-size:14px;color:var(--cream)">${esc(k.name)}</span>
                  ${isArchived(k) ? archivedPill() : ''}
                  ${ps.map(p => programPill(p.id)).join('')}
                </div>
                <div style="font-size:12px;color:var(--muted);margin-top:3px">DOB ${k.dob ? fmtD(k.dob) : '—'} · Age ${calcAge(k.dob)}${k.gender ? ' · ' + esc(k.gender) : ''}${k.grade ? ' · ' + esc(k.grade) + ' grade' : ''}${k.school ? ' · ' + esc(k.school) : ''}</div>
              </div>
              ${canEdit(session) ? `<button class="btn-ghost btn-sm" onclick="viewPerformer('${k.id}')">Manage</button>` : ''}
            </div>`;
          }).join('') : '<p class="empty">No performers on this account.</p>'}
        </div>

        ${(() => {
          const acContracts = (DB.contracts || []).filter(c => c.accountId === u.id)
            .slice().sort((a, b) => (a.programName || '').localeCompare(b.programName || '') || b.seasonYear - a.seasonYear);
          return `<div class="card">
          <div class="card-hd">Contracts</div>
          ${acContracts.length ? acContracts.map(c => `
            <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid var(--border-soft)">
              <div>
                <div style="font-size:14px;color:var(--cream)">${esc(c.programName || '—')}</div>
                <div style="font-size:11px;color:var(--muted);margin-top:2px">${c.seasonYear} season</div>
              </div>
              <div style="display:flex;align-items:center;gap:10px;flex-shrink:0">
                ${c.signed ? '<span class="pill pill-paid" style="font-size:10px">Signed</span>' : '<span class="pill pill-off" style="font-size:10px">Awaiting</span>'}
                ${c.signed && c.firmaSigningRequestId ? `<button class="btn-ghost btn-sm" data-action="download-contract" data-contract="${c.id}">View PDF</button>` : ''}
              </div>
            </div>`).join('') : '<p class="empty">No contracts on record.</p>'}
        </div>`;
        })()}
      </div>

      <div style="display:flex;flex-direction:column;gap:18px">
        <div class="card">
          <div class="card-hd">Card on file ${canEdit(session) ? `<button class="btn-ghost btn-sm" data-action="card-modal-for-user" data-account="${u.id}">Update</button>` : ''}</div>
          ${u.card
            ? `<div style="display:flex;align-items:center;gap:14px">
                <div style="display:flex;align-items:center;justify-content:center;min-width:44px;flex-shrink:0">${CARD_LOGOS[u.card.brand] || `<span style="font-size:11px;font-weight:700;color:#333">${esc(u.card.brand)}</span>`}</div>
                <div>
                  <div style="font-size:14px;color:var(--cream)">${esc(u.card.brand)} ending in ${esc(u.card.last4)}</div>
                  <div style="font-size:12px;color:var(--muted);margin-top:2px">Expires ${esc(u.card.exp)}</div>
                </div>
               </div>`
            : `<p style="font-size:13px;color:var(--muted)">No card on file.</p>`}
        </div>
        <div class="card">
          <div class="card-hd">Stripe ${canEdit(session) ? `<button class="btn-ghost btn-sm" data-action="stripe-modal" data-account="${u.id}">${linked ? 'Manage' : 'Link Stripe'}</button>` : ''}</div>
          ${linked
            ? `<div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap"><span class="pill pill-paid">Linked</span><a href="https://dashboard.stripe.com/customers/${esc(u.stripeId)}" target="_blank" rel="noopener" style="font-size:13px;color:var(--gold);font-family:monospace;text-decoration:none">${esc(u.stripeId)} <span style="font-family:'Source Sans 3',sans-serif">&#8599;</span></a></div>
               <div style="font-size:11px;color:var(--muted);margin-top:8px">Opens this customer in the Stripe Dashboard.</div>`
            : `<p style="font-size:13px;color:var(--muted)">No Stripe customer linked to this account.</p>`}
        </div>
        <div class="card">
        <div class="card-hd">Billing history ${due ? `<span style="font-size:12px;color:var(--red);font-weight:400">${fmt$(due)} due</span>` : ''}</div>
        ${invoices.length ? invoices.map(([date, rows]) => {
          const total   = rows.reduce((s,c) => s + c.amount, 0);
          const allSame = rows.every(r => r.status === rows[0].status);
          const status  = allSame ? rows[0].status : 'mixed';
          const pillCls = status === 'paid' ? 'pill-paid' : status === 'due' ? 'pill-due' : status === 'refunded' ? 'pill-refunded' : 'pill-off';
          const pillLbl = status === 'mixed' ? 'partial' : status;
          const hasDue  = rows.some(c => c.status === 'due');
          // Compact one-liner: date · performer (program) pairs · status · total
          const parts = rows.map(c => {
            const nm = childName(c.childId);
            const prog = (PROGRAMS.find(p => p.id === c.programId) || {}).name;
            return nm ? (prog ? `${nm} (${prog})` : nm) : esc(c.desc);
          });
          const summary = [...new Set(parts)].map(esc).join(' · ');
          return `
          <div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--border-soft)">
            <span style="font-size:12px;color:var(--cream);font-weight:500;white-space:nowrap;flex-shrink:0">${fmtD(date)}</span>
            <span style="flex:1;min-width:0;font-size:12px;color:var(--muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${summary}</span>
            <span class="pill ${pillCls}" style="font-size:10px;flex-shrink:0">${pillLbl}</span>
            <span class="amt" style="font-size:13px;flex-shrink:0${hasDue ? ';color:var(--red)' : ''}">${fmt$(total)}</span>
          </div>`;
        }).join('') : '<p class="empty">No charges on record.</p>'}
        ${allInvoices.length > 4 ? `<button onclick="route('a-parent-history')" style="margin-top:14px;width:100%;padding:8px;background:transparent;border:1px dashed rgba(201,168,76,0.3);border-radius:7px;color:var(--gold);font-size:13px;font-family:inherit;cursor:pointer">Show all payment history &rsaquo;</button>` : ''}
        </div>

        ${(() => {
          const emails = DB.emailLog.filter(e =>
            (e.accountId && e.accountId === u.id) ||
            (e.toEmail && e.toEmail.toLowerCase() === u.email.toLowerCase())
          );
          const shown = emails.slice(0, 10);
          return `<div class="card">
          <div class="card-hd">Emails</div>
          ${shown.length ? shown.map(e => `
            <div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--border-soft)">
              <span style="font-size:12px;color:var(--cream);font-weight:500;white-space:nowrap;flex-shrink:0">${e.date ? fmtD(e.date) : '—'}</span>
              <span style="flex:1;min-width:0">
                <div style="font-size:13px;color:var(--cream)">${emailLabel(e.type)}</div>
                <div style="font-size:11px;color:var(--muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(e.subject)}</div>
              </span>
              <span class="pill ${e.status === 'sent' ? 'pill-paid' : 'pill-due'}" style="font-size:10px;flex-shrink:0">${esc(e.status)}</span>
            </div>`).join('')
          : '<p class="empty">No emails on record.</p>'}
          ${emails.length > 10 ? `<button onclick="route('a-email-log')" style="margin-top:14px;width:100%;padding:8px;background:transparent;border:1px dashed rgba(201,168,76,0.3);border-radius:7px;color:var(--gold);font-size:13px;font-family:inherit;cursor:pointer">View all email activity &rsaquo;</button>` : ''}
        </div>`;
        })()}
      </div>
    </div>`;
};

VIEW_INIT['a-parent-detail'] = () => {
  document.querySelectorAll('[data-action="card-modal-for-user"]').forEach(btn => {
    btn.addEventListener('click', () => cardModalForUser(btn.dataset.account));
  });
  document.querySelectorAll('[data-action="send-invitation"]').forEach(btn => {
    btn.addEventListener('click', () => sendInvitation(btn.dataset.account));
  });
  document.querySelectorAll('[data-action="resend-invitation"]').forEach(btn => {
    btn.addEventListener('click', () => resendInvitation(btn.dataset.account));
  });
  document.querySelectorAll('[data-action="toggle-active"]').forEach(btn => {
    btn.addEventListener('click', () => toggleActive(btn.dataset.account));
  });
  document.querySelectorAll('[data-action="stripe-modal"]').forEach(btn => {
    btn.addEventListener('click', () => stripeModal(btn.dataset.account));
  });
  document.querySelectorAll('[data-action="download-contract"]').forEach(btn => {
    btn.addEventListener('click', () => downloadContract(btn.dataset.contract));
  });
  document.querySelectorAll('[data-action="charge-card-confirm"]').forEach(btn => {
    btn.addEventListener('click', () => chargeCardConfirm(btn.dataset.account, btn.dataset.name, Number(btn.dataset.total)));
  });
  document.querySelectorAll('[data-action="acct-modal"]').forEach(btn => {
    btn.addEventListener('click', () => acctModal(btn.dataset.account));
  });
  document.querySelectorAll('[data-action="view-as-parent"]').forEach(btn => {
    btn.addEventListener('click', () => viewAsParent(btn.dataset.account));
  });
  document.querySelectorAll('[data-action="archive-family"]').forEach(btn => {
    btn.addEventListener('click', () => archiveFamilyModal(btn.dataset.account));
  });
  document.querySelectorAll('[data-action="unarchive-family"]').forEach(btn => {
    btn.addEventListener('click', () => unarchiveFamily(btn.dataset.id));
  });
};

let acSort = { col: 'name', dir: 'asc' };
let acSearch = '';
let acStatus = 'active';

function acSorted() {
  let rows = DB.users.filter(u => isParent(u)).map(u => {
    const kids = DB.children.filter(c => c.accountId === u.id);
    const due  = DB.charges.filter(c => c.accountId === u.id && c.status === 'due').reduce((s,c) => s + c.amount, 0);
    const state = inviteState(u);
    const statusOrder = state === 'active' ? 0 : state === 'invited' ? 1 : 2;
    return {
      ...u,
      name:        u.firstName + ' ' + u.lastName,
      performers:  kids.map(k => k.name).join(', '),
      kids,
      due,
      lastLoginSort: u.lastLogin || '',
      stripeLinked: !!(u.stripeId && u.stripeId.startsWith('cus_')),
      statusSort:  u.active ? statusOrder : (isArchived(u) ? 4 : 3)
    };
  });
  // Status filter. Archived accounts are deactivated as a side effect of being
  // archived, so "Deactivated" has to exclude them explicitly or the two
  // filters would show the same families and neither would mean anything.
  if (acStatus === 'active')      rows = rows.filter(r => r.active);
  if (acStatus === 'deactivated') rows = rows.filter(r => !r.active && !isArchived(r));
  if (acStatus === 'archived')    rows = rows.filter(r => isArchived(r));
  // search
  if (acSearch) {
    const q = acSearch.toLowerCase();
    rows = rows.filter(r =>
      r.name.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      (r.phone || '').includes(q) ||
      r.performers.toLowerCase().includes(q)
    );
  }
  // sort
  rows.sort((a, b) => {
    let av = a[acSort.col] ?? '', bv = b[acSort.col] ?? '';
    if (acSort.col === 'due' || acSort.col === 'statusSort') { av = +av; bv = +bv; }
    else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
    return acSort.dir === 'asc' ? (av > bv ? 1 : av < bv ? -1 : 0) : (av < bv ? 1 : av > bv ? -1 : 0);
  });
  return rows;
}
function acSetSort(col) {
  if (acSort.col === col) acSort.dir = acSort.dir === 'asc' ? 'desc' : 'asc';
  else { acSort.col = col; acSort.dir = 'asc'; }
  renderAccountTable();
}
function acSetSearch(val) {
  acSearch = val;
  const btn = document.getElementById('acClearBtn');
  if (btn) btn.style.display = val ? 'block' : 'none';
  renderAccountTable();
}
function acClearSearch() {
  acSearch = '';
  const inp = document.getElementById('acSearchInput');
  if (inp) { inp.value = ''; inp.focus(); }
  const btn = document.getElementById('acClearBtn');
  if (btn) btn.style.display = 'none';
  renderAccountTable();
}
function acSetStatus(val) { acStatus = val; renderAccountTable(); }

function renderAccountTable() {
  const rows = acSorted();
  const th = (col, label) => {
    const cls = acSort.col === col ? acSort.dir : '';
    return `<th class="sort-th ${cls}" data-action="ac-set-sort" data-col="${col}">${label}<span class="sort-arrow"></span></th>`;
  };
  const total = DB.users.filter(u => isParent(u)).length;
  const archivedCount = DB.users.filter(u => isParent(u) && isArchived(u)).length;
  document.getElementById('acCountLabel').textContent =
    `${rows.length === total ? 'All' : rows.length + ' of'} ${total} account${total !== 1 ? 's' : ''}`
    + (archivedCount && acStatus !== 'archived' ? ` · ${archivedCount} archived` : '');
  const notInvited = DB.users.filter(u => isParent(u) && u.active && inviteState(u) === 'not_invited').length;
  const inviteBtn = document.getElementById('bulkInviteBtn');
  if (inviteBtn) {
    inviteBtn.textContent = notInvited ? `Send invitations (${notInvited})` : 'Send invitations';
    inviteBtn.disabled = !notInvited;
    inviteBtn.style.opacity = notInvited ? '1' : '0.4';
    inviteBtn.style.cursor = notInvited ? 'pointer' : 'not-allowed';
  }
  document.getElementById('acTableWrap').innerHTML = `
    <div class="tbl-wrap"><table>
      <thead><tr>
        ${th('name','Parent')}
        ${th('email','Contact')}
        ${th('performers','Performers')}
        ${th('due','Balance due')}
        ${th('statusSort','Status')}
        ${th('lastLoginSort','Last login')}
      </tr></thead>
      <tbody>
        ${rows.length ? rows.map(u => `<tr onclick="viewParent('${u.id}')" style="cursor:pointer">
          <td><button class="tbl-link">${esc(u.name)}</button></td>
          <td>${esc(u.email)}<div class="sub">${esc(u.phone)}</div></td>
          <td>${u.kids.length ? u.kids.map(k => `<button class="tbl-link" onclick="event.stopPropagation();viewPerformer('${k.id}')" style="${isArchived(k) ? 'color:var(--muted)' : ''}">${esc(k.name)}</button>`).join('<span style="color:var(--muted)">, </span>') : '<span class="sub">None</span>'}</td>
          <td class="amt" style="${u.due ? 'color:var(--red)' : ''}">${fmt$(u.due)}</td>
          <td>${u.active ? inviteStatePill(inviteState(u)) : (isArchived(u) ? '<span class="pill pill-off">Archived</span>' : '<span class="pill pill-off">Deactivated</span>')}</td>
          <td style="font-size:12px;color:var(--muted);white-space:nowrap">${fmtLocalDT(u.lastLogin)}</td>
        </tr>`).join('') : `<tr><td colspan="6" style="padding:20px 12px"><p class="empty" style="padding:0">No accounts match these filters.</p></td></tr>`}
      </tbody>
    </table></div>`;
  document.querySelectorAll('#acTableWrap [data-action="ac-set-sort"]').forEach(th => {
    th.addEventListener('click', () => acSetSort(th.dataset.col));
  });
}

VIEWS['a-accounts'] = () => {
  acSearch = ''; acStatus = 'active';
  const html = `
    <div class="page-head">
      <h2 class="serif">Parents</h2>
    </div>
    <div class="card">
      <div class="card-hd">
        <span id="acCountLabel"></span>
        ${canEdit(session) ? `<span style="display:inline-flex;gap:8px">
          <button class="btn-ghost btn-sm" id="bulkInviteBtn"></button>
          <button class="btn-ghost btn-sm" id="acctModalNewBtn">+ New account</button>
        </span>` : ''}
      </div>
      <div class="filters" style="margin-bottom:16px;align-items:flex-end">
        <div class="field" style="flex:1;min-width:200px;margin-bottom:0">
          <label>Search</label>
          <div style="position:relative">
            <input type="text" id="acSearchInput" placeholder="Name, email, phone, or performer…" style="height:38px;padding-right:28px;width:100%;box-sizing:border-box">
            <button id="acClearBtn" aria-label="Clear search" style="position:absolute;right:6px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:var(--muted);font-size:18px;line-height:1;padding:2px 4px;display:none">×</button>
          </div>
        </div>
        <div class="field" style="min-width:160px;margin-bottom:0">
          <label>Status</label>
          <select id="acStatusSelect" style="height:38px">
            <option value="active">Active</option>
            <option value="deactivated">Deactivated</option>
            <option value="archived">Archived</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>
      <div id="acTableWrap"></div>
    </div>`;
  setTimeout(renderAccountTable, 0);
  return html;
};

VIEW_INIT['a-accounts'] = () => {
  // bulkInviteBtn/acctModalNewBtn/acSearchInput/acClearBtn/acStatusSelect
  // are never rewritten by renderAccountTable() (only #acTableWrap and
  // #acCountLabel are) — a one-time attach here is sufficient.
  const inviteBtn = document.getElementById('bulkInviteBtn');
  if (inviteBtn) inviteBtn.addEventListener('click', bulkInvite);
  const newBtn = document.getElementById('acctModalNewBtn');
  if (newBtn) newBtn.addEventListener('click', () => acctModal());
  const searchInp = document.getElementById('acSearchInput');
  if (searchInp) searchInp.addEventListener('input', () => acSetSearch(searchInp.value));
  const clearBtn = document.getElementById('acClearBtn');
  if (clearBtn) clearBtn.addEventListener('click', acClearSearch);
  const statusSel = document.getElementById('acStatusSelect');
  if (statusSel) statusSel.addEventListener('change', () => acSetStatus(statusSel.value));
};

function acctModal(userId) {
  const u = userId ? DB.users.find(x => x.id === userId) : null;
  openModal(`
    <h3>${u ? 'Edit account' : 'New parent account'}</h3>
    <div class="field-row">
      <div class="field"><label>First name</label><input type="text" id="amFirst" value="${u ? esc(u.firstName) : ''}"></div>
      <div class="field"><label>Last name</label><input type="text" id="amLast" value="${u ? esc(u.lastName) : ''}"></div>
    </div>
    <div class="field"><label>Email</label><input type="email" id="amEmail" value="${u ? esc(u.email) : ''}">
      <span class="error-msg" id="amEmailErr">Please enter a valid email address.</span></div>
    <div class="field"><label>Phone</label><input type="tel" id="amPhone" value="${u ? esc(u.phone) : ''}"></div>
    <div class="field"><label>Address</label><input type="text" id="amAddress" value="${u ? esc(u.address || '') : ''}" placeholder="123 Main St"></div>
    <div class="field-row">
      <div class="field"><label>City</label><input type="text" id="amCity" value="${u ? esc(u.city || '') : ''}" placeholder="Orem"></div>
      <div class="field"><label>State</label>
        <select id="amState">
          <option value="">—</option>
          ${US_STATES.map(st => `<option value="${st}" ${u && u.state === st ? 'selected' : ''}>${st}</option>`).join('')}
        </select>
      </div>
      <div class="field"><label>ZIP</label><input type="text" id="amZip" inputmode="numeric" value="${u ? esc(u.zip || '') : ''}" placeholder="84057"></div>
    </div>
    <button class="btn" id="amSaveBtn" style="margin-top:6px">${u ? 'Save changes' : 'Create account'}</button>`);
  document.getElementById('amSaveBtn').addEventListener('click', () => saveAcct(u ? u.id : ''));
}
// The admin modal and the New-account modal share saveAcct, so the address
// fields are read in one place rather than spelled out in both payloads.
function addressPayload() {
  const v = id => (document.getElementById(id)?.value || '').trim();
  return { address: v('amAddress'), city: v('amCity'), state: v('amState'), zip: v('amZip') };
}
async function saveAcct(userId) {
  const f = id => document.getElementById(id).value.trim();
  if (!f('amFirst')) { toast('Name is required'); return; }
  const emailErr = document.getElementById('amEmailErr');
  if (!isValidEmail(f('amEmail'))) { if (emailErr) emailErr.classList.add('show'); return; }
  if (emailErr) emailErr.classList.remove('show');
  try {
    if (userId) {
      const updated = await apiFetch('/accounts/' + userId, {
        method: 'PUT',
        body: JSON.stringify({ firstName: f('amFirst'), lastName: f('amLast'), email: f('amEmail'), phone: f('amPhone'), ...addressPayload() })
      });
      const idx = DB.users.findIndex(x => x.id === userId);
      if (idx >= 0) DB.users[idx] = updated; else DB.users.push(updated);
      closeModal();
      if (currentView === 'a-accounts') renderAccountTable(); else refresh();
      toast('Account updated');
    } else {
      const created = await apiFetch('/accounts', {
        method: 'POST',
        body: JSON.stringify({ firstName: f('amFirst'), lastName: f('amLast'), email: f('amEmail'), phone: f('amPhone'), ...addressPayload(), roles: ['parent'] })
      });
      DB.users.push(created);
      closeModal();
      if (currentView === 'a-accounts') renderAccountTable(); else refresh();
      toast(created.warning || 'Account created — invitation email sent');
    }
  } catch (e) { toastError(e.message); }
}

// renderAccountTable is called directly (not via onclick) from
// admin-invitations.js's confirmBulkInvite/saveStripeId/unlinkStripe/
// toggleActive — a pre-existing cross-file JS-call pattern, unrelated to
// the onclick migration, so it stays window-exposed.
Object.assign(window, { renderAccountTable });
